import React from 'react';

export type StudioIconName =
  | 'quill'
  | 'refresh'
  | 'download'
  | 'library'
  | 'structure'
  | 'script'
  | 'world'
  | 'theme'
  | 'character'
  | 'arc'
  | 'scene'
  | 'event'
  | 'device'
  | 'dialogue'
  | 'search'
  | 'board'
  | 'canvas'
  | 'lanes'
  | 'assistant'
  | 'inspector'
  | 'spark';

interface StudioIconProps {
  name: StudioIconName;
  className?: string;
}

export const StudioIcon: React.FC<StudioIconProps> = ({ name, className }) => {
  const commonProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    'aria-hidden': true,
  };

  switch (name) {
    case 'quill':
      return (
        <svg {...commonProps}>
          <path d="M20 4c-3 0-5 1.1-6.7 2.8L6 14v4h4l7.2-7.3C18.9 9 20 7 20 4Z" />
          <path d="M8 16 4 20" />
          <path d="M13 7l4 4" />
        </svg>
      );
    case 'refresh':
      return (
        <svg {...commonProps}>
          <path d="M20 6v5h-5" />
          <path d="M4 18v-5h5" />
          <path d="M7.5 8.2A7 7 0 0 1 19 11" />
          <path d="M16.5 15.8A7 7 0 0 1 5 13" />
        </svg>
      );
    case 'download':
      return (
        <svg {...commonProps}>
          <path d="M12 4.5v10" />
          <path d="m8.5 11.5 3.5 3.5 3.5-3.5" />
          <path d="M5 18.5h14" />
        </svg>
      );
    case 'library':
      return (
        <svg {...commonProps}>
          <path d="M5 5.5h10.5A2.5 2.5 0 0 1 18 8v10.5" />
          <path d="M8 4v14" />
          <path d="M12 6v12" />
          <path d="M16 8v10" />
          <path d="M5 18h13" />
        </svg>
      );
    case 'structure':
      return (
        <svg {...commonProps}>
          <rect x="4" y="5" width="6" height="6" rx="1.5" />
          <rect x="14" y="5" width="6" height="6" rx="1.5" />
          <rect x="4" y="15" width="6" height="5" rx="1.5" />
          <path d="M14 17.5h6" />
          <path d="M14 20h4" />
          <path d="M17 11v4" />
        </svg>
      );
    case 'script':
      return (
        <svg {...commonProps}>
          <path d="M8 3.5h6l4 4V20a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 7 20V5a1.5 1.5 0 0 1 1-1.4Z" />
          <path d="M14 3.5V8h4" />
          <path d="M10 12h6" />
          <path d="M10 15.5h6" />
          <path d="M10 19h4" />
        </svg>
      );
    case 'world':
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="12" r="8" />
          <path d="M4 12h16" />
          <path d="M12 4a13 13 0 0 1 3 8 13 13 0 0 1-3 8 13 13 0 0 1-3-8 13 13 0 0 1 3-8Z" />
        </svg>
      );
    case 'theme':
      return (
        <svg {...commonProps}>
          <path d="M12 3.5 13.8 8l4.7.4-3.6 2.8 1.2 4.5L12 13.4l-4.1 2.3 1.2-4.5-3.6-2.8L10.2 8 12 3.5Z" />
        </svg>
      );
    case 'character':
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="8" r="3.2" />
          <path d="M6.5 19a5.5 5.5 0 0 1 11 0" />
        </svg>
      );
    case 'arc':
      return (
        <svg {...commonProps}>
          <path d="M5 17c3-8 11-11 14-10" />
          <path d="M16 5h3v3" />
          <circle cx="8" cy="16" r="1.5" />
          <circle cx="18" cy="7" r="1.5" />
        </svg>
      );
    case 'scene':
      return (
        <svg {...commonProps}>
          <rect x="4" y="6" width="16" height="11" rx="2" />
          <path d="M8 19h8" />
          <path d="M10 17v2" />
          <path d="M14 17v2" />
          <path d="M7.5 8.5 10.5 11l-3 2.5" />
          <path d="M16.5 8.5 13.5 11l3 2.5" />
        </svg>
      );
    case 'event':
      return (
        <svg {...commonProps}>
          <path d="M13 3 6 13h5l-1 8 8-11h-5l0-7Z" />
        </svg>
      );
    case 'device':
      return (
        <svg {...commonProps}>
          <path d="M12 3.5 18.5 12 12 20.5 5.5 12 12 3.5Z" />
          <path d="M9.5 12h5" />
          <path d="M12 9.5v5" />
        </svg>
      );
    case 'dialogue':
      return (
        <svg {...commonProps}>
          <path d="M6 6.5h12A2.5 2.5 0 0 1 20.5 9v6A2.5 2.5 0 0 1 18 17.5H11l-4 3v-3H6A2.5 2.5 0 0 1 3.5 15V9A2.5 2.5 0 0 1 6 6.5Z" />
          <path d="M8 11h8" />
          <path d="M8 14h5" />
        </svg>
      );
    case 'search':
      return (
        <svg {...commonProps}>
          <circle cx="11" cy="11" r="5.5" />
          <path d="m19 19-3.5-3.5" />
        </svg>
      );
    case 'board':
      return (
        <svg {...commonProps}>
          <rect x="4" y="4.5" width="16" height="15" rx="2" />
          <path d="M4 10.5h16" />
          <path d="M10.5 10.5v9" />
          <path d="M10.5 4.5v6" />
        </svg>
      );
    case 'canvas':
      return (
        <svg {...commonProps}>
          <rect x="4" y="5" width="16" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.4" />
          <path d="m11 16 3.2-3.8 2.8 3 2-2.2" />
          <path d="M7 16h2.5" />
        </svg>
      );
    case 'lanes':
      return (
        <svg {...commonProps}>
          <rect x="4" y="5" width="16" height="4" rx="1.4" />
          <rect x="4" y="10.5" width="16" height="4" rx="1.4" />
          <rect x="4" y="16" width="16" height="4" rx="1.4" />
          <path d="M8 5v15" />
          <path d="M15 10.5v9.5" />
        </svg>
      );
    case 'assistant':
      return (
        <svg {...commonProps}>
          <path d="M12 3.5 13.5 8l4.5 1.5-4.5 1.5L12 15.5 10.5 11 6 9.5 10.5 8 12 3.5Z" />
          <path d="M18 14.5 18.8 17l2.7.8-2.7.9-.8 2.5-.8-2.5-2.7-.9 2.7-.8.8-2.5Z" />
          <path d="M6 14.5 6.7 16.5 8.7 17.2l-2 .7-.7 2-.7-2-2-.7 2-.7.7-2Z" />
        </svg>
      );
    case 'inspector':
      return (
        <svg {...commonProps}>
          <path d="M5 6.5h14" />
          <path d="M5 12h14" />
          <path d="M5 17.5h14" />
          <circle cx="9" cy="6.5" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="11" cy="17.5" r="1.5" />
        </svg>
      );
    case 'spark':
      return (
        <svg {...commonProps}>
          <path d="M12 5v4" />
          <path d="M12 15v4" />
          <path d="M5 12h4" />
          <path d="M15 12h4" />
          <path d="m7.5 7.5 2.5 2.5" />
          <path d="m14 14 2.5 2.5" />
          <path d="m16.5 7.5-2.5 2.5" />
          <path d="M10 14 7.5 16.5" />
        </svg>
      );
    default:
      return null;
  }
};
